import { latestActiveRoot } from './presentation.js';
import type { ConversationSnapshot } from './types.js';

export type FeedbackSnapshot = Pick<ConversationSnapshot, 'runs' | 'uiMessages' | 'toolCalls' | 'question' | 'activity'> & {
  messages: Omit<ConversationSnapshot['messages'][number], 'appId'>[];
};
export type ChatFeedback = { label: string; active: boolean; details: string[] };

/** Only retained, browser-authorized projections may describe agent activity. */
export function chatFeedback(snapshot: FeedbackSnapshot | null, options: {
  disconnected?: boolean; stopping?: boolean; pending?: boolean; liveText?: string;
  terminal?: boolean;
} = {}): ChatFeedback | null {
  const feedback = (label: string, active = true, details: string[] = []): ChatFeedback => ({ label, active, details });
  if (options.disconnected) return feedback('Reconnecting…');
  const root = snapshot ? latestActiveRoot(snapshot.runs) : null;
  if (options.stopping || root?.status === 'cancelling') return feedback('Stopping…');
  if (!root) {
    if (options.pending) return feedback('Working…');
    const latest = snapshot?.runs.filter(run => run.parentRunId === null).at(-1);
    if (options.terminal && latest?.status === 'failed') return feedback('Couldn’t finish', false);
    if (options.terminal && latest?.status === 'cancelled') return feedback('Stopped', false);
    return null;
  }
  if (snapshot?.question?.runId === root.id || root.status === 'waiting_for_user') return null;
  if (root.status === 'queued') return feedback('Waiting to start…');
  if (root.status === 'provisioning' || root.phase === 'starting' || root.status === 'resuming' && root.phase !== 'understanding') return feedback('Starting your agent…');
  if (root.status === 'paused' || root.status === 'pausing') return feedback(root.blocker?.requiredAction === 'wait' ? 'Waiting for the AI provider…' : 'Agent paused', false);
  const latestUser = snapshot?.messages.filter(message => message.role === 'user').at(-1);
  const currentTurn = (createdAt: string) => !latestUser || createdAt >= latestUser.createdAt;
  const messages = snapshot?.uiMessages?.filter(message => message.role === 'assistant' && message.metadata.runId === root.id && message.metadata.presentation !== 'progress' && currentTurn(message.metadata.createdAt)) ?? [];
  const streaming = options.liveText?.trim() || messages.some(message => message.parts.some(part => part.type === 'text' && part.text.trim() && part.state === 'streaming'));
  if (streaming) return null;
  const tools = (snapshot?.toolCalls ?? []).filter(tool => tool.runId === root.id);
  const runningTool = [...tools].reverse().find(tool => tool.status === 'running');
  const reasoning = messages.flatMap(message => message.parts.flatMap(part => part.type === 'reasoning' && part.text.trim() ? [part.text] : []));
  if (runningTool) return feedback(['testing', 'verifying'].includes(root.phase) ? 'Running checks…' : 'Using tools…', true, tools.slice(-3).map(tool => tool.summary).filter(Boolean));
  // A finished response may precede the runtime settling (including its warm idle).
  if (messages.some(message => message.parts.some(part => part.type === 'text' && part.text.trim())) ||
      snapshot?.messages.some(message => message.role === 'agent' && message.runId === root.id && message.presentation !== 'progress' && currentTurn(message.createdAt))) return null;
  if (['testing', 'verifying'].includes(root.phase)) return feedback('Running checks…', true, (snapshot?.activity ?? []).filter(item => item.runId === root.id && item.kind === 'check').slice(-3).map(item => item.label));
  return feedback(root.phase === 'understanding' || reasoning.length ? 'Thinking…' : 'Working…', true, reasoning.slice(-1));
}

/** Merge streaming text by its stable message identity, including interrupted text. */
export function standaloneTurns(snapshot: FeedbackSnapshot | null) {
  const turns = new Map<string, { id: string; role: 'user' | 'agent'; content: string; createdAt: string }>();
  for (const message of snapshot?.messages ?? []) {
    if (message.presentation === 'progress') continue;
    turns.set(message.id, { id: message.id, role: message.role === 'user' ? 'user' : 'agent', content: message.content, createdAt: message.createdAt });
  }
  for (const message of snapshot?.uiMessages ?? []) {
    if (message.role !== 'assistant' || message.metadata.presentation === 'progress') continue;
    const id = message.metadata.sourceMessageId ?? message.id;
    if (turns.has(id)) continue;
    const content = message.parts.flatMap(part => part.type === 'text' ? [part.text] : []).join('');
    if (content.trim()) turns.set(id, { id, role: 'agent', content, createdAt: message.metadata.createdAt });
  }
  return [...turns.values()].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}
